/**
 * Time Tracking API Service
 * Handles all time tracking related API calls
 */

import axiosClient from '../../../api/axiosClient';
import {
  TimeLog,
  TimeSummary,
  TimeReport,
  CreateTimeLogDTO,
  UpdateTimeLogDTO,
  StartTimerDTO,
  StopTimerDTO,
  TimeTrackingFilters,
} from '../model/types';
import {
  mockTimeLogs,
  mockTimeSummary,
  mockTimeReport,
  getMockTimeLog,
  getMockTaskTimeLogs,
  getMockUserTimeLogs,
} from './mockTimeTrackingData';

// In-memory storage for mock active timer
let currentMockActiveTimer: any = null;

export const timeTrackingService = {
  /**
   * Create a new time log entry (manual)
   */
  createTimeLog: async (data: CreateTimeLogDTO): Promise<TimeLog> => {
    try {
      const response = await axiosClient.post('/time-logs', {
        ...data,
        totalMinutes: data.hours * 60 + data.minutes,
        date: data.date || new Date().toISOString(),
      });
      return response.data;
    } catch (error) {
      console.warn('🔄 Backend not available, creating mock time log');
      const newLog: TimeLog = {
        id: `log-${Date.now()}`,
        taskId: data.taskId,
        userId: 'user-1',
        userName: 'Current User',
        hours: data.hours,
        minutes: data.minutes,
        totalMinutes: data.hours * 60 + data.minutes,
        notes: data.notes,
        date: data.date || new Date().toISOString(),
        isActive: false,
        createdAt: new Date().toISOString(),
      };
      mockTimeLogs.push(newLog);
      return newLog;
    }
  },

  /**
   * Get time logs with optional filters
   */
  getTimeLogs: async (filters?: TimeTrackingFilters): Promise<TimeLog[]> => {
    try {
      const params = new URLSearchParams();
      if (filters?.taskId) params.append('taskId', filters.taskId);
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.sprintId) params.append('sprintId', filters.sprintId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));

      const response = await axiosClient.get(`/time-logs?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.warn('🔄 Backend not available, using mock time logs');
      let filteredLogs = [...mockTimeLogs];
      if (filters?.taskId) filteredLogs = filteredLogs.filter(log => log.taskId === filters.taskId);
      if (filters?.userId) filteredLogs = filteredLogs.filter(log => log.userId === filters.userId);
      if (filters?.isActive !== undefined) filteredLogs = filteredLogs.filter(log => log.isActive === filters.isActive);
      return filteredLogs;
    }
  },

  /**
   * Get time logs for a specific task
   */
  getTaskTimeLogs: async (taskId: string): Promise<TimeLog[]> => {
    try {
      const response = await axiosClient.get(`/tasks/${taskId}/time-logs`);
      return response.data;
    } catch (error) {
      console.warn('🔄 Backend not available, using mock task time logs');
      return getMockTaskTimeLogs(taskId);
    }
  },

  /**
   * Get a single time log by ID
   */
  getTimeLog: async (logId: string): Promise<TimeLog> => {
    try {
      const response = await axiosClient.get(`/time-logs/${logId}`);
      return response.data;
    } catch (error) {
      console.warn('🔄 Backend not available, using mock time log');
      const log = getMockTimeLog(logId);
      if (!log) throw new Error(`Time log ${logId} not found in mock data`);
      return log;
    }
  },

  /**
   * Update an existing time log
   */
  updateTimeLog: async (logId: string, updates: UpdateTimeLogDTO): Promise<TimeLog> => {
    try {
      const payload: any = { ...updates };
      if (updates.hours !== undefined && updates.minutes !== undefined) {
        payload.totalMinutes = updates.hours * 60 + updates.minutes;
      }

      const response = await axiosClient.put(`/time-logs/${logId}`, payload);
      return response.data;
    } catch (error) {
      console.warn('🔄 Backend not available, updating mock time log');
      const logIndex = mockTimeLogs.findIndex(log => log.id === logId);
      if (logIndex === -1) throw new Error(`Time log ${logId} not found`);

      const updatedLog = {
        ...mockTimeLogs[logIndex],
        ...updates,
        totalMinutes: updates.hours !== undefined && updates.minutes !== undefined
          ? updates.hours * 60 + updates.minutes
          : mockTimeLogs[logIndex].totalMinutes,
        updatedAt: new Date().toISOString(),
      };

      mockTimeLogs[logIndex] = updatedLog;
      return updatedLog;
    }
  },

  /**
   * Delete a time log
   */
  deleteTimeLog: async (logId: string): Promise<void> => {
    try {
      await axiosClient.delete(`/time-logs/${logId}`);
    } catch (error) {
      console.warn('🔄 Backend not available, deleting mock time log');
      const logIndex = mockTimeLogs.findIndex(log => log.id === logId);
      if (logIndex !== -1) {
        mockTimeLogs.splice(logIndex, 1);
      }
    }
  },

  /**
   * Start a timer for a task
   */
  startTimer: async (data: StartTimerDTO): Promise<TimeLog> => {
    try {
      const response = await axiosClient.post('/time-logs/timer/start', {
        ...data,
        startTime: new Date().toISOString(),
        isActive: true,
      });
      return response.data;
    } catch (error) {
      console.warn('🔄 Backend not available, starting mock timer');
      const newTimer: TimeLog = {
        id: `timer-${Date.now()}`,
        taskId: data.taskId,
        userId: 'user-1',
        userName: 'Current User',
        taskName: data.taskId, // Will be updated when task name is available
        hours: 0,
        minutes: 0,
        totalMinutes: 0,
        notes: data.notes,
        date: new Date().toISOString(),
        startTime: new Date().toISOString(),
        isActive: true,
        createdAt: new Date().toISOString(),
      };
      currentMockActiveTimer = newTimer;
      return newTimer;
    }
  },

  /**
   * Stop the active timer
   */
  stopTimer: async (logId: string, data?: StopTimerDTO): Promise<TimeLog> => {
    try {
      const response = await axiosClient.post(`/time-logs/${logId}/timer/stop`, {
        ...data,
        endTime: new Date().toISOString(),
      });
      return response.data;
    } catch (error) {
      console.warn('🔄 Backend not available, stopping mock timer');
      if (currentMockActiveTimer) {
        const endTime = new Date();
        const startTime = new Date(currentMockActiveTimer.startTime);
        const totalMinutes = Math.floor((endTime.getTime() - startTime.getTime()) / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        const stoppedLog: TimeLog = {
          ...currentMockActiveTimer,
          endTime: endTime.toISOString(),
          hours,
          minutes,
          totalMinutes,
          isActive: false,
          notes: data?.notes || currentMockActiveTimer.notes,
          updatedAt: endTime.toISOString(),
        };

        mockTimeLogs.push(stoppedLog);
        currentMockActiveTimer = null;
        return stoppedLog;
      }
      throw new Error('No active timer to stop');
    }
  },

  /**
   * Pause the active timer
   */
  pauseTimer: async (logId: string): Promise<TimeLog> => {
    try {
      const response = await axiosClient.post(`/time-logs/${logId}/timer/pause`, {
        pausedAt: new Date().toISOString(),
      });
      return response.data;
    } catch (error) {
      console.warn('🔄 Backend not available, pause not supported in mock mode');
      throw new Error('Pause functionality requires backend connection');
    }
  },

  /**
   * Resume a paused timer
   */
  resumeTimer: async (logId: string): Promise<TimeLog> => {
    try {
      const response = await axiosClient.post(`/time-logs/${logId}/timer/resume`);
      return response.data;
    } catch (error) {
      console.warn('🔄 Backend not available, resume not supported in mock mode');
      throw new Error('Resume functionality requires backend connection');
    }
  },

  /**
   * Get the currently active timer
   */
  getActiveTimer: async (): Promise<TimeLog | null> => {
    try {
      const response = await axiosClient.get('/time-logs/timer/active');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.warn('🔄 Backend not available, using mock active timer');
      return currentMockActiveTimer;
    }
  },

  /**
   * Get time summary for a project/board
   */
  getTimeSummary: async (boardId: string, filters?: TimeTrackingFilters): Promise<TimeSummary> => {
    try {
      const params = new URLSearchParams();
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.sprintId) params.append('sprintId', filters.sprintId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const response = await axiosClient.get(`/boards/${boardId}/time-summary?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.warn('🔄 Backend not available, using mock time summary');
      return mockTimeSummary;
    }
  },

  /**
   * Generate time report
   */
  generateReport: async (
    boardId: string,
    period: 'day' | 'week' | 'sprint' | 'month' | 'custom',
    startDate?: string,
    endDate?: string
  ): Promise<TimeReport> => {
    try {
      const params = new URLSearchParams();
      params.append('period', period);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await axiosClient.get(`/boards/${boardId}/time-report?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.warn('🔄 Backend not available, using mock time report');
      return mockTimeReport;
    }
  },

  /**
   * Export time report
   */
  exportReport: async (
    boardId: string,
    format: 'csv' | 'excel' | 'pdf',
    filters?: TimeTrackingFilters
  ): Promise<Blob> => {
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.sprintId) params.append('sprintId', filters.sprintId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const response = await axiosClient.get(`/boards/${boardId}/time-report/export?${params.toString()}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.warn('🔄 Backend not available, export not supported in mock mode');
      throw new Error('Export functionality requires backend connection');
    }
  },

  /**
   * Get user's time logs
   */
  getUserTimeLogs: async (userId: string, filters?: TimeTrackingFilters): Promise<TimeLog[]> => {
    try {
      const params = new URLSearchParams();
      if (filters?.taskId) params.append('taskId', filters.taskId);
      if (filters?.sprintId) params.append('sprintId', filters.sprintId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const response = await axiosClient.get(`/users/${userId}/time-logs?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.warn('🔄 Backend not available, using mock user time logs');
      return getMockUserTimeLogs(userId);
    }
  },
};

export default timeTrackingService;
